import React, { useId, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import HelpOutline from '@mui/icons-material/HelpOutline';

const sections = [
  { title: 'Individual readings', description: 'Heart rate, HRV, skin temperature and step readings retain their recorded timestamps. Gaps in readings remain gaps; sample spacing does not establish continuous wear time.' },
  { title: 'Daily summaries', description: 'Daily steps and HRV summaries retain the values reported by Ultrahuman. They are separate views of the measurements, so adding a daily summary to its individual readings can double-count data.' },
  { title: 'Weekly activity snapshots', description: 'Weekly active minutes is the weekly total reported for each date. These overlapping snapshots must not be added across dates or treated as daily active minutes.' },
];

export default function UltrahumanMeasurementGuide({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return <>
    {compact ? <Tooltip title="How do these measurements relate?">
      <IconButton size="small" aria-label="How do these measurements relate?" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
        <HelpOutline fontSize="small" />
      </IconButton>
    </Tooltip> : <Button size="small" startIcon={<HelpOutline />} onClick={() => setOpen(true)}
      aria-haspopup="dialog" aria-expanded={open} sx={{ alignSelf: 'flex-start', px: 0, textTransform: 'none' }}>
      How do these measurements relate?
    </Button>}
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm"
      aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`} PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle id={`${id}-title`}>Ultrahuman measurements</DialogTitle>
      <DialogContent>
        <DialogContentText id={`${id}-description`} sx={{ mb: 2 }}>
          Choose the Ultrahuman reading or summary that matches your research question. Each retains its own units and reporting period.
        </DialogContentText>
        <Stack gap={1.5}>{sections.map(section => <Box key={section.title} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography fontWeight={650}>{section.title}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>{section.description}</Typography>
        </Box>)}</Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Availability depends on the participant and dates. Unverified metrics keep their warning even if a similarly named measurement has been reviewed for another device.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}><Button autoFocus onClick={() => setOpen(false)}>Got it</Button></DialogActions>
    </Dialog>
  </>;
}
